import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useHistory } from 'react-router-dom';
import { useForgetComplectationModal } from '@features/modals/comlectation-forget';
import Scroll from 'react-scroll';
import { type ICity } from '@entities/cities';
import useUserIsExist from 'frontend/data/user/useUserExist';
import styles from './placeOrder.module.scss';
import ButtonPlaceOrder from './ButtonPlaceOrder';
import { deleteProductsHandler, loginHandler } from '../lib';

const scrollToTop = () => {
  Scroll.animateScroll.scrollTo(0, {
    duration: 300,
    delay: 100,
  });
};

interface IPlaceOrder {
  isPickupProducts: boolean;
  isDeliveryProducts: boolean;
  city: ICity;
  total?: number;
}

const getTextPlaceOrder = ({
  t,
  isPickupProducts,
  isDeliveryProducts,
}: {
  t: TFunction;
  isPickupProducts: boolean;
  isDeliveryProducts: boolean;
}) => {
  if (isPickupProducts && isDeliveryProducts)
    return t('NewCart.bothRestriction');
  if (isPickupProducts) return t('NewCart.pickupRestriction');
  if (isDeliveryProducts) return t('NewCart.delivery_restriction');
};

export function PlaceOrder({
  isPickupProducts,
  isDeliveryProducts,
  city,
  total = 0,
}: IPlaceOrder) {
  const { t } = useTranslation();
  const { isUserExist } = useUserIsExist();
  const history = useHistory();
  const modal = useForgetComplectationModal();

  const {
    available_for_order: availableForOrder,
    auth_before_ordering: isLoginNeed,
    is_delivery: isDeliveryInCity,
    is_pickup: isPickupInCity,
    text_id: cityTextId,
    info: {
      delivery: { order_min: orderMin },
    },
  } = city;

  const isMinAmount = total < orderMin;
  const isDisableBtnPickup = !availableForOrder || !isPickupInCity;
  const isDisableBtnDelivery =
    isMinAmount || !isDeliveryInCity || !availableForOrder;

  const actions = {
    delivery: {
      path: `/${cityTextId}/cart/delivery`,
      shouldDelete: isPickupProducts,
    },
    pickup: {
      path: `/${cityTextId}/cart/pickup`,
      shouldDelete: isDeliveryProducts,
    },
  };

  const handleClick = (type: keyof typeof actions) => {
    const { path, shouldDelete } = actions[type];
    const afterAction = (success: boolean) => {
      if (!success) {
        scrollToTop();
        return;
      }
      history.push(path);
    };

    modal.hide();

    if (!isUserExist && isLoginNeed) {
      loginHandler(afterAction);
      return;
    }
    if (shouldDelete) {
      deleteProductsHandler({ type, handleAfterModal: afterAction });
      return;
    }

    history.push(path);
  };

  const textPlaceOrder = getTextPlaceOrder({
    t,
    isPickupProducts,
    isDeliveryProducts,
  });

  return (
    <div className={styles.placeOrder}>
      <h3 className={styles.title}>{t('localization.placeYourOrder')}</h3>
      {textPlaceOrder && <p className={styles.warning}>{textPlaceOrder}</p>}

      <div className={styles.buttons}>
        <ButtonPlaceOrder
          disabled={isDisableBtnDelivery}
          onClick={() => handleClick('delivery')}
        >
          {t('NewCart.delivery')}
        </ButtonPlaceOrder>
        <ButtonPlaceOrder
          disabled={isDisableBtnPickup}
          onClick={() => handleClick('pickup')}
        >
          {t('NewCart.pickup')}
        </ButtonPlaceOrder>
      </div>
    </div>
  );
}
